import { countLabel } from '@/utils/plural'
import EmptyState from '@components/EmptyState'
import { PublicSectionGate } from '@components/PublicPage'
import { usePublicItinerary } from '@hooks'
import { GetPublicItineraryResponse } from '@orbit/api-types'
import ItineraryBoard from '@pages/Itinerary/ItineraryBoard'
import { useItineraryModel } from '@pages/Itinerary/useItineraryModel'
import { useSelectedDay } from '@pages/Itinerary/useSelectedDay'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PublicSectionLayout from '../PublicSections/PublicSectionLayout'
import { publicTripMapPath } from './paths'

/**
 * Public, read-only itinerary at `/shared/trips/:tripCode/itinerary`: the
 * member itinerary card in public mode — the same calendar, day and map with
 * every way to edit absent — inside the public section chrome, behind the
 * shared gate for loading / unavailable / error states.
 */
const PublicItineraryPage: React.FC = () => {
	const { tripCode = '' } = useParams<{ tripCode: string }>()
	const query = usePublicItinerary(tripCode)

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading itinerary…"
			unavailableTitle="Itinerary Unavailable"
			unavailableMessage="This itinerary doesn't exist or has been set to private by the trip owner."
		>
			{data => <PublicItinerary tripCode={tripCode} data={data} />}
		</PublicSectionGate>
	)
}

/** The happy path, split out so its hooks run only once the gate has data. */
const PublicItinerary: React.FC<{ tripCode: string; data: GetPublicItineraryResponse }> = ({ tripCode, data }) => {
	const navigate = useNavigate()
	const { trip } = data
	const dayCount = trip.dayCount ?? 0
	const { selectedDay, selectDay } = useSelectedDay(null, { startDate: trip.startDate, dayCount })
	const model = useItineraryModel({
		trip,
		events: data.events,
		dayInfos: data.tripDayInfo,
		segments: data.travelSegments,
		selectedDay,
	})

	return (
		<PublicSectionLayout tripCode={tripCode} title="Itinerary" subtitle={countLabel(dayCount, 'day')} fill>
			{dayCount === 0 ? (
				<EmptyState art="itinerary" title="No itinerary yet" description="This trip does not have itinerary dates yet." />
			) : (
				<ItineraryBoard
					model={model}
					onSelectDay={selectDay}
					onOpenFullMap={dayIndex => navigate(publicTripMapPath(tripCode, dayIndex))}
				/>
			)}
		</PublicSectionLayout>
	)
}

export default PublicItineraryPage
